/** Locale + theme preferences (persisted, applied to <html>). */
import { resolveLocale, type Locale } from "@bundle/i18n";
import type { KeyValueStorage } from "@bundle/core";
import { AccentColor } from "@bundle/contracts";
import { PreferencesContext, type ThemePreference } from "@bundle/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";

const LOCALE_KEY = "pref.locale";
const THEME_KEY = "pref.theme";
const ACCENT_KEY = "pref.accent";

export function initialLocale(storage: KeyValueStorage): Locale {
  const saved = storage.get(LOCALE_KEY);
  if (saved === "en" || saved === "pt-BR") return saved;
  return resolveLocale(navigator.languages);
}

export function PreferencesProvider({ storage, children, onLocale }: { storage: KeyValueStorage; children: (locale: Locale) => ReactNode; onLocale?: (l: Locale) => void }) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale(storage));
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    const t = storage.get(THEME_KEY);
    return t === "light" || t === "dark" ? t : "system";
  });
  const [accent, setAccentState] = useState<AccentColor>(() => AccentColor.catch("indigo").parse(storage.get(ACCENT_KEY)));
  useEffect(() => {
    // The default (indigo) is the base palette: no attribute.
    if (accent === "indigo") document.documentElement.removeAttribute("data-accent");
    else document.documentElement.setAttribute("data-accent", accent);
  }, [accent]);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.lang = locale;
    onLocale?.(locale);
  }, [locale, onLocale]);
  const value = useMemo(
    () => ({
      locale,
      theme,
      setLocale: (l: Locale) => {
        storage.set(LOCALE_KEY, l);
        setLocaleState(l);
      },
      setTheme: (t: ThemePreference) => {
        storage.set(THEME_KEY, t);
        setThemeState(t);
      },
      accent,
      setAccent: (a: AccentColor) => {
        storage.set(ACCENT_KEY, a);
        setAccentState(a);
      },
    }),
    [locale, theme, accent, storage],
  );
  return <PreferencesContext.Provider value={value}>{children(locale)}</PreferencesContext.Provider>;
}
